import { ReactNode, createContext, FC, useContext, useMemo } from 'react';
import { SystemContext } from './SystemContext';
import {
  Section,
  SectionKey,
  ApplicationDisplayConfig,
  SECTION_TO_CONFIG_KEY,
} from './ApplicationDisplayContext.types';

// Re-export types and constants for convenience
export { Section, type SectionKey, type ApplicationDisplayConfig };

/**
 * Context value containing display configuration
 */
interface ApplicationDisplayContextValue {
  config: ApplicationDisplayConfig;

  // Helper methods
  canShowSection: (section: SectionKey) => boolean;
  canShowResourceType: (resourceType: string) => boolean;
}

const ApplicationDisplayContext = createContext<
  ApplicationDisplayContextValue | undefined
>(undefined);

type Props = {
  children: ReactNode;
};

/**
 * Determines display configuration based on application attributes
 * @param app - iTRC application data
 * @param getPolicyAccess - Function to check policy access
 * @returns Display configuration object
 */
function computeDisplayConfig(
  app: any,
  getPolicyAccess: (policy: string) => boolean,
): ApplicationDisplayConfig {
  const isExperimental = app?.status === 'Experimental';
  const status = app?.status;
  const corporateEntity = app?.corporateEntity;

  // Experimental applications have limited features
  if (isExperimental) {
    return {
      showResources: true,
      showMetrics: false,
      showOwnership: true,
      showLifecycle: true,
      showProvisioning: true,
      showGreenEnergy: false,
      showPrivacy: false,
      showShowback: false,
      showAuditEvents: false,
      showDependency: false,
      showIAM: false,
      showSecurity: false,
      allowedResourceTypes: ['adgroup', 'github-repo', 'cnap-tenancy'],
      canEdit: false,
      canDelete: true,
      canProvision: false, // Already provisioned during creation
      canRetire: false,
      canManageTags: false,
      isExperimental: true,
      statusBadgeVariant: 'info',
      displayMode: 'compact',
    };
  }

  // Standard applications - full features based on policies
  const baseConfig: ApplicationDisplayConfig = {
    showResources: true,
    showMetrics: true,
    showOwnership: true,
    showLifecycle: true,
    showProvisioning: true,
    showGreenEnergy: true,
    showPrivacy: true,
    showShowback: getPolicyAccess('ShowbackTabPolicy'),
    showAuditEvents: getPolicyAccess('AccessAuditEventsPolicy'),
    showDependency: true,
    showIAM: true,
    showSecurity: true,
    allowedResourceTypes: [], // Empty = all types allowed
    canEdit: getPolicyAccess('UpdateApplicationPolicy'),
    canDelete: false, // Typically disabled for production apps
    canProvision: true,
    canRetire: getPolicyAccess('RetireApplicationPolicy'),
    canManageTags: getPolicyAccess('ManageApplicationTagsPolicy'),
    isExperimental: false,
    statusBadgeVariant: 'success',
    displayMode: 'full',
  };

  // Adjust based on status
  if (status === 'Retired' || status === 'Deprecated') {
    baseConfig.canEdit = false;
    baseConfig.canProvision = false;
    baseConfig.statusBadgeVariant = 'warning';
    baseConfig.displayMode = 'readonly';
  }

  // Adjust based on corporate entity
  if (corporateEntity === 'LEGACY') {
    baseConfig.showProvisioning = false;
    baseConfig.displayMode = 'readonly';
  }

  return baseConfig;
}

/**
 * Provider component that computes and exposes application display configuration.
 * Must be nested inside SystemProvider to access application data and policies.
 * @param props - Component props
 * @param props.children - Child components to render
 */
export const ApplicationDisplayProvider: FC<Props> = ({ children }) => {
  const { app, loading, getPolicyAccess } = useContext(SystemContext);

  const value = useMemo<ApplicationDisplayContextValue>(() => {
    // Don't compute config until app data is loaded
    if (loading || !app) {
      return {
        config: {} as ApplicationDisplayConfig,
        canShowSection: () => false,
        canShowResourceType: () => false,
      };
    }

    const config = computeDisplayConfig(app, getPolicyAccess);

    return {
      config,
      canShowSection: section => {
        const configKey = SECTION_TO_CONFIG_KEY[section];
        if (configKey && typeof config[configKey] === 'boolean') {
          return config[configKey] as boolean;
        }
        return true;
      },
      canShowResourceType: resourceType => {
        if (config.allowedResourceTypes.length === 0) {
          return true; // All types allowed
        }
        return config.allowedResourceTypes.includes(resourceType);
      },
    };
  }, [app, loading, getPolicyAccess]);

  return (
    <ApplicationDisplayContext.Provider value={value}>
      {children}
    </ApplicationDisplayContext.Provider>
  );
};

/**
 * Hook to access application display configuration
 */
export const useApplicationDisplay = (): ApplicationDisplayContextValue => {
  const context = useContext(ApplicationDisplayContext);
  if (!context) {
    throw new Error(
      'useApplicationDisplay must be used within ApplicationDisplayProvider',
    );
  }
  return context;
};
